/**
 * The Clients screen (f6.1) -- the third hierarchy level.
 *
 * This replaces f1.1's phase placeholder behind the `clients` navigation key,
 * which f1.3 already granted to every role and `navigation.ts` already labels
 * *"Phase 6"*. **f1.3's role-to-navigation mapping is not edited**, and f1.1's
 * shared placeholder component is left alone for the keys still using it.
 *
 * **What the prototype draws that is deliberately absent**, so a reviewer can
 * check the list rather than file defects:
 *
 * | Prototype element | Why it is not here | Owner |
 * | --- | --- | --- |
 * | The `Engagements` count | **Built.** f7.1 delivered `engagement_count`, resolved server-side in ONE query over every client, so the column renders a real number where f6.1 rendered an em dash. It counts ACTIVE engagements, and the cell links through to the client's Engagements tab | -- |
 * | `Export` | No requirement names an export on this screen; f13.1's is per cycle | -- not planned |
 * | The 39 seeded clients | adr-16 forbids seeding hierarchy; the table ships empty | f8.2, f8.3 |
 * | The prototype's sample values | Blackstone is given `SAP 10442`; its real `Account: SAPID` is **19710**. Its inactive sample row, `MQG` / Macquarie Group Ltd, names a client that is not one of the 39 -- it is an `FS sheet` pipeline row. The layout is kept; the data is not | -- |
 *
 * **Every filter and the pagination run client-side.** The search box, the
 * four selects and the prev/1/2/3/next controls all work over the collection
 * the server returned whole, because it is 39 rows and the endpoint takes no
 * parameter of any kind -- which keeps f4.1's assertion true for a third
 * feature. When the collection outgrows the browser the route grows a
 * parameter; that condition is written down in `clientView.ts`.
 *
 * Every value on screen is bound to the server's answer. Nothing here derives
 * a status label, a currency name, a permission or a visibility rule in the
 * browser.
 */

import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  AlertTriangle,
  Ban,
  Briefcase,
  ChevronLeft,
  ChevronRight,
  Pencil,
  Plus,
  RotateCcw,
  RotateCw,
  Search,
  ShieldAlert,
} from 'lucide-react';

import type { Client } from '@/api/client';
import { useBusinessUnits, useClients, useReactivateClient } from '@/api/queries';
import { useSessionScope } from '@/session/SessionScopeContext';
import { CREATE_CHILD, EDIT_NODE, MANAGE_HIERARCHY, rowCan } from '@/session/permissions';
import { EmptyCell } from '@/ui/EmptyCell';
import { ScopedEmptyState } from '@/ui/ScopedEmptyState';
import { GroupParentLink, UnitParentLink } from '@/ui/ScopedParentLink';
import { SortableHeader } from '@/ui/SortableHeader';
import { nextSort, type SortState } from '@/ui/tableSort';

import { ClientDialog } from './ClientDialog';
import { DeactivateClientDialog } from './DeactivateClientDialog';
import {
  DEFAULT_SORT,
  INHERITED_CM,
  NO_FILTERS,
  badgeClassFor,
  clientSummary,
  dmFor,
  dmOptions,
  groupOptions,
  inheritedOwnerFor,
  initialsOf,
  isActive,
  isRefusal,
  pageNumbers,
  pageOf,
  statusOptions,
  unitOptions,
  visibleClients,
  type ClientFilters,
  type SortColumn,
} from './clientView';

/** The Business Units screen, for the "create a parent first" state. */
const UNITS_PATH = '/business-units';

/** The status code an ACTIVE parent carries. The label is still the server's. */
const STATUS_ACTIVE = 'ACTIVE';

/**
 * What the `Engagements` column means, for the reader who wonders which count
 * it is.
 *
 * The legacy has two and they differ: the master workbook gives 70 CURRENT
 * engagements while the Kimble feed gives 141 SINCE 2017. The screen means the
 * former, so a client that ran three engagements in 2018 and none now reads
 * zero rather than three.
 */
const ENGAGEMENTS_TITLE =
  'Active engagements recorded in Atlas. Historical ones that have been deactivated are not counted.';

function PageHead({ subtitle, action }: { subtitle?: string; action?: ReactNode }) {
  return (
    <div className="page-head">
      <div>
        <h1 className="page-title">Clients</h1>
        <div className="page-sub">
          {subtitle ??
            'Third level of the hierarchy, and where engagements, commitments and artifacts live'}
        </div>
      </div>
      <div className="page-actions">{action}</div>
    </div>
  );
}

function StateCard({
  icon,
  warning = false,
  title,
  children,
}: {
  icon: ReactNode;
  warning?: boolean;
  title: string;
  children: ReactNode;
}) {
  return (
    <div className="card">
      <div className="card-body">
        <div className="empty">
          <div className={`e-icon${warning ? ' e-icon-warning' : ''}`}>{icon}</div>
          <h3>{title}</h3>
          {children}
        </div>
      </div>
    </div>
  );
}

function NoAccess() {
  return (
    <>
      <PageHead />
      <StateCard warning icon={<ShieldAlert aria-hidden="true" />} title="No access">
        <p>
          The server refuses this area for your account, and records the refusal in the
          audit log.
        </p>
      </StateCard>
    </>
  );
}

/** Bars, not an empty table: distinguishable from a list with no rows. */
function LoadingSkeleton() {
  return (
    <>
      <PageHead />
      <div className="card" aria-busy="true" aria-label="Loading Clients">
        <div className="card-body">
          {[0, 1, 2, 3, 4].map((index) => (
            <span key={index} className="skeleton-bar group-skeleton-line" />
          ))}
        </div>
      </div>
    </>
  );
}

/**
 * f9.2: the route is guarded by `RequirePermittedNavigation` in `App.tsx`, so
 * this screen no longer repeats the navigation check. Presentation only, as
 * ever: every endpoint below authorizes on its own authority.
 */
export function ClientsPage() {
  const clients = useClients();
  /*
   * The parents a create can name. Read here rather than inside the dialog so
   * the `New client` control can be disabled -- with a reason -- when there is
   * no active Business Unit to be the required first field's value.
   */
  const units = useBusinessUnits();
  const { can } = useSessionScope();
  /*
   * SESSION-level: "may this caller write anything" decides whether the
   * list-level control is rendered. Each row's controls come from its own
   * `permissions`, and the create dialog's parents from theirs.
   */
  const mayManage = can(MANAGE_HIERARCHY);

  /*
   * The Business Units screen links through with `?unit=<id>`. It is a
   * CLIENT-side filter seeded from the URL, not a server parameter: the
   * collection endpoint takes none, and f4.1's assertion that no hierarchy
   * operation accepts a filter still holds verbatim.
   */
  const [params] = useSearchParams();
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState<ClientFilters>(() => ({
    ...NO_FILTERS,
    unitId: params.get('unit') ?? '',
  }));
  const [sort, setSort] = useState<SortState<SortColumn>>(DEFAULT_SORT);
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState<Client | null>(null);
  const [creating, setCreating] = useState(false);
  const [deactivating, setDeactivating] = useState<Client | null>(null);

  const all = clients.data?.clients ?? [];
  const visible = useMemo(
    () => visibleClients(all, { search, filters, sort }),
    [all, search, filters, sort],
  );
  const current = useMemo(() => pageOf(visible, page), [visible, page]);

  /*
   * Narrowing the list can strand the reader on a page that no longer exists.
   * `pageOf` already clamps what it RENDERS; this keeps the state in step, so
   * the highlighted page button agrees with the rows below it.
   */
  useEffect(() => {
    if (current.page !== page) setPage(current.page);
  }, [current.page, page]);

  const activeUnits = useMemo(
    () =>
      (units.data?.business_units ?? []).filter(
        (unit) => unit.status.code === STATUS_ACTIVE,
      ),
    [units.data],
  );
  /*
   * The parents a create may name: active, AND carrying the server's
   * `hierarchy:create_child` for this caller. Never re-derived here.
   */
  const parentUnits = useMemo(
    () => activeUnits.filter((unit) => rowCan(unit, CREATE_CHILD)),
    [activeUnits],
  );

  if (clients.isPending || units.isPending) return <LoadingSkeleton />;

  const failure = clients.error ?? units.error;
  if (failure) {
    /*
     * A 403 is a refusal, not a failure -- and it never triggers the
     * interactive re-authentication a 401 does (f1.2).
     */
    if (isRefusal(failure)) return <NoAccess />;
    return (
      <>
        <PageHead />
        <StateCard
          warning
          icon={<AlertTriangle aria-hidden="true" />}
          title="The Clients could not be loaded"
        >
          <p>{failure instanceof Error ? failure.message : 'Unexpected error.'}</p>
          <p className="empty-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => {
                void clients.refetch();
                void units.refetch();
              }}
            >
              <RotateCw aria-hidden="true" /> Retry
            </button>
          </p>
        </StateCard>
      </>
    );
  }

  const toggleSort = (column: SortColumn) => {
    setSort((state) => nextSort(state, column));
    setPage(1);
  };
  const setFilter = (field: keyof ClientFilters, value: string) => {
    setFilters((state) => ({ ...state, [field]: value }));
    setPage(1);
  };

  /*
   * Opening a dialog whose required first field has no options is a dead end,
   * so the control is disabled and SAYS WHY rather than being hidden.
   */
  const canCreate = mayManage && parentUnits.length > 0;
  const createTitle = !mayManage || canCreate
    ? undefined
    : activeUnits.length === 0
      ? 'Create an active Business Unit first: every client must have one.'
      : 'None of the Business Units you can see allows you to create a client under it.';

  return (
    <>
      <PageHead
        subtitle={
          /*
           * The prototype's subtitle, computed rather than typed: it reads
           * "39 clients across 9 Business Units" only when the data says so.
           */
          all.length === 0
            ? 'Third level of the hierarchy, and where engagements, commitments and artifacts live'
            : `${clientSummary(all)} · engagements live on the client`
        }
        action={
          mayManage ? (
            <button
              type="button"
              className="btn btn-primary"
              disabled={!canCreate}
              title={createTitle}
              onClick={() => setCreating(true)}
            >
              <Plus aria-hidden="true" /> New client
            </button>
          ) : undefined
        }
      />

      <div className="toolbar">
        <div className="search-field">
          <Search aria-hidden="true" />
          <input
            type="search"
            value={search}
            placeholder="Search client, code or SAP ID"
            aria-label="Search client, code or SAP ID"
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(1);
            }}
          />
        </div>
        {/*
          Options are the values actually present, not every value the server
          knows: a filter offering a unit no row carries is a worse control
          than one offering the ones that do (f4.2's rule).
        */}
        <select
          className="toolbar-select"
          aria-label="Filter by Business Unit Group"
          value={filters.groupId}
          onChange={(event) => setFilter('groupId', event.target.value)}
        >
          <option value="">All Business Unit Groups</option>
          {groupOptions(all).map((option) => (
            <option key={option.id} value={option.id}>
              {option.name}
            </option>
          ))}
        </select>
        <select
          className="toolbar-select"
          aria-label="Filter by Business Unit"
          value={filters.unitId}
          onChange={(event) => setFilter('unitId', event.target.value)}
        >
          <option value="">All Business Units</option>
          {unitOptions(all).map((option) => (
            <option key={option.id} value={option.id}>
              {option.name}
            </option>
          ))}
        </select>
        <select
          className="toolbar-select"
          aria-label="Filter by status"
          value={filters.statusCode}
          onChange={(event) => setFilter('statusCode', event.target.value)}
        >
          <option value="">All statuses</option>
          {statusOptions(all).map((option) => (
            <option key={option.code} value={option.code}>
              {option.name}
            </option>
          ))}
        </select>
        <select
          className="toolbar-select"
          aria-label="Filter by DM"
          value={filters.dmUserId}
          onChange={(event) => setFilter('dmUserId', event.target.value)}
        >
          <option value="">All DMs</option>
          {dmOptions(all).map((option) => (
            <option key={option.userId} value={option.userId}>
              {option.displayName}
            </option>
          ))}
        </select>
      </div>

      <div className="card">
        <div className="table-scroll">
          <table className="data zebra">
            <thead>
              <tr>
                <SortableHeader column="code" label="Code" sort={sort} onSort={toggleSort} />
                <SortableHeader column="name" label="Client" sort={sort} onSort={toggleSort} />
                <SortableHeader
                  column="unit"
                  label="Business Unit"
                  sort={sort}
                  onSort={toggleSort}
                />
                <SortableHeader
                  column="group"
                  label="Business Unit Group"
                  sort={sort}
                  onSort={toggleSort}
                />
                <SortableHeader column="sapId" label="SAP ID" sort={sort} onSort={toggleSort} />
                <SortableHeader column="DM" label="DM" sort={sort} onSort={toggleSort} />
                <SortableHeader column="CM" label="CM" sort={sort} onSort={toggleSort} />
                <SortableHeader
                  column="engagements"
                  label="Engagements"
                  className="num"
                  sort={sort}
                  onSort={toggleSort}
                />
                <SortableHeader column="status" label="Status" sort={sort} onSort={toggleSort} />
                <th scope="col">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {current.rows.length === 0 ? (
                <tr>
                  <td colSpan={10}>
                    <ScopedEmptyState
                      searching={all.length > 0}
                      matchNoun="clients"
                      collectionNoun="clients"
                      icon={<Briefcase aria-hidden="true" />}
                      freshInstallHint="Create the first client, or wait for the master workbook import to bring the 39 across."
                      parentMissing={
                        activeUnits.length > 0
                          ? null
                          : {
                              title: 'No Business Unit exists yet',
                              hint: (
                                <>
                                  Every client hangs off a Business Unit, so a unit has to exist
                                  first.{' '}
                                  <Link className="cell-link" to={UNITS_PATH}>
                                    Create one on the Business Units screen
                                  </Link>
                                  .
                                </>
                              ),
                            }
                      }
                    />
                  </td>
                </tr>
              ) : (
                current.rows.map((row) => (
                  <ClientRow
                    key={row.id}
                    client={row}
                    onEdit={() => setEditing(row)}
                    onDeactivate={() => setDeactivating(row)}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/*
        The prototype's page controls, over the loaded set. No route gained a
        parameter to make this work, which is the whole point of doing it here.
      */}
      {/*
        Not a disclosure: `total` counts the collection the server ALREADY
        scoped, after the caller's own search and filters. It reports the
        caller's narrowing, never the server's.
      */}
      <div className="pagination">
        <span className="info">
          {current.total === 0
            ? 'No clients to show'
            : `Showing ${current.from}–${current.to} of ${current.total} ${
                current.total === 1 ? 'client' : 'clients'
              }`}
        </span>
        {current.pageCount > 1 && (
          <div className="pg-btns">
            <button
              type="button"
              className="pg-btn"
              aria-label="Previous page"
              disabled={current.page === 1}
              onClick={() => setPage(current.page - 1)}
            >
              <ChevronLeft aria-hidden="true" />
            </button>
            {pageNumbers(current.pageCount).map((number) => (
              <button
                type="button"
                key={number}
                className={number === current.page ? 'pg-btn is-active' : 'pg-btn'}
                aria-label={`Page ${number}`}
                aria-current={number === current.page ? 'page' : undefined}
                onClick={() => setPage(number)}
              >
                {number}
              </button>
            ))}
            <button
              type="button"
              className="pg-btn"
              aria-label="Next page"
              disabled={current.page === current.pageCount}
              onClick={() => setPage(current.page + 1)}
            >
              <ChevronRight aria-hidden="true" />
            </button>
          </div>
        )}
      </div>

      {(creating || editing) && (
        <ClientDialog
          client={editing}
          units={parentUnits}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
        />
      )}
      {deactivating && (
        <DeactivateClientDialog
          client={deactivating}
          onClose={() => setDeactivating(null)}
        />
      )}
    </>
  );
}

function OwnerCell({ name }: { name: string }) {
  return (
    <span className="row owner-cell">
      <span className="avatar sm" aria-hidden="true">
        {initialsOf(name)}
      </span>
      {name}
    </span>
  );
}

function ClientRow({
  client,
  onEdit,
  onDeactivate,
}: {
  client: Client;
  onEdit: () => void;
  onDeactivate: () => void;
}) {
  const reactivate = useReactivateClient();
  const busy = reactivate.isPending;
  const active = isActive(client);
  /* The ROW's own capability, projected by the server from f9.1's grant table. */
  const mayEdit = rowCan(client, EDIT_NODE);
  const dm = dmFor(client);
  const cm = inheritedOwnerFor(client, INHERITED_CM);

  return (
    <tr>
      <td className="mono">{client.code}</td>
      <td>
        <Link className="cell-link" to={`/clients/${client.id}`}>
          {client.name}
        </Link>
        {/*
          ALL the aliases, not one. The prototype's detail screen draws three
          chips; the real set is whatever f8.3 and an operator record.
        */}
        {client.aliases.length > 0 && (
          <span className="sub">
            {client.aliases.length === 1 ? 'alias: ' : 'aliases: '}
            {client.aliases.join(', ')}
          </span>
        )}
      </td>
      <td>
        <UnitParentLink unit={client.business_unit} />
      </td>
      <td>
        <GroupParentLink group={client.business_unit_group} />
      </td>
      <td className="mono">{client.sap_id ?? <EmptyCell />}</td>
      <td>
        {dm ? (
          <OwnerCell name={dm.display_name} />
        ) : (
          /*
           * The prototype's badge, kept. It reports the ABSENCE OF AN
           * ASSIGNMENT and nothing more -- never a client-side inference about
           * permissions.
           */
          <span className="badge pill-warning">Not assigned</span>
        )}
      </td>
      <td>
        {/*
          INHERITED from the Business Unit and read-only here: the control that
          changes it lives on the screen that owns it. Display only -- it
          decides nothing, and f9.1 owns the walk-down that does.
        */}
        {cm ? <OwnerCell name={cm.display_name} /> : <EmptyCell />}
      </td>
      <td className="num" title={ENGAGEMENTS_TITLE}>
        {/*
          A real number since f7.1, resolved server-side in ONE query over
          every client. It links to the Engagements TAB rather than to a
          screen, because there is no engagements screen -- an engagement is
          administered inside Client detail and never from the navigation.
        */}
        <Link className="cell-link" to={`/clients/${client.id}`}>
          {client.engagement_count}
        </Link>
      </td>
      <td>
        <span className={badgeClassFor(client.status.code)}>{client.status.name}</span>
      </td>
      <td>
        <div className="row-actions">
          {mayEdit && (
            <>
              <button
                type="button"
                className="btn btn-secondary btn-icon"
                title="Edit"
                aria-label={`Edit ${client.name}`}
                disabled={busy}
                onClick={onEdit}
              >
                <Pencil aria-hidden="true" />
              </button>
              {active ? (
                <button
                  type="button"
                  className="btn btn-secondary btn-icon"
                  title="Deactivate"
                  aria-label={`Deactivate ${client.name}`}
                  disabled={busy}
                  onClick={onDeactivate}
                >
                  <Ban aria-hidden="true" />
                </button>
              ) : (
                <button
                  type="button"
                  className="btn btn-secondary btn-icon"
                  title="Reactivate"
                  aria-label={`Reactivate ${client.name}`}
                  disabled={busy}
                  /*
                   * Unguarded and unconfirmed: reactivating a parent cannot
                   * orphan anything, and it grants nothing new.
                   */
                  onClick={() => reactivate.mutate(client.id)}
                >
                  <RotateCcw aria-hidden="true" />
                </button>
              )}
            </>
          )}
        </div>
      </td>
    </tr>
  );
}
